import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';

/**
 * Connected Apps — card spotlight-081.
 *
 * OAuth-minted MCP tokens (Claude, Codex, Cursor, etc.) currently
 * land on the same /keys list as sk/pk admin keys, but they're a
 * different concept: they aren't minted by the operator, they're
 * issued by an external client via the OAuth consent flow.  This
 * page surfaces them on a dedicated surface with reauthorize +
 * revoke affordances, and lets the operator kick off a new
 * "Connect Claude" flow from inside the dashboard rather than
 * trip out to claude.ai to do it.
 *
 * Data shape:
 *
 *   GET /v1/admin/api-keys?kind=mcp
 *
 * Each row carries the same fields as any other api-key row —
 * scopes, last_used_at, owner_user_id, etc. — so we can reuse the
 * server-side role hierarchy / metadata-only protection without
 * additional surface area.  The `kind=mcp` filter is the only new
 * backend hook (see admin_api_keys.py).
 *
 * Reauthorize: today we treat it as a "kick the OAuth consent flow
 * again" affordance, since the actual token rotation lives on the
 * external client (Claude / Codex / Cursor).  Clicking takes the
 * operator to /oauth/authorize?client=<id> with the existing
 * client_id baked in, and they re-grant scopes.  Revoke is a
 * standard DELETE on the api-key row.
 */
export function ConnectedApps() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // kind=mcp surfaces only OAuth-minted rows; the
      // role-hierarchy + ownership filters on the backend still apply
      // so a sub-admin only sees their own connections.
      const resp = await api('/v1/admin/api-keys?kind=mcp');
      setRows(resp?.keys || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const onRevoke = useCallback(async (row) => {
    const friendly = appLabel(row);
    const ok = await ask({
      title: `Disconnect ${friendly}?`,
      // We never call it "delete" because the external client may
      // still cache the token; "disconnect" matches what the user
      // sees on their side after they re-launch the integration.
      body:
        `This breaks ${friendly}'s access to this tenant immediately. The ` +
        'connected app will need to reauthorize before it can read or ' +
        'write again. Other tokens stay untouched.',
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    try {
      await api(
        `/v1/admin/api-keys/${encodeURIComponent(row.prefix)}`,
        { method: 'DELETE' },
      );
      await load();
    } catch (e) {
      setError(e.message);
    }
  }, [api, ask, load]);

  const onReauthorize = useCallback((row) => {
    // /oauth/authorize is the consent surface (see OAuthAuthorize.jsx).
    // We pre-fill the client identifier so the flow lands on the
    // right app row; the actual scope picker still defaults from
    // /oauth/scopes so the operator can adjust if needed.
    const clientId = oauthClientIdFor(row);
    const url = `/oauth/authorize?client=${encodeURIComponent(clientId)}`;
    window.location.assign(url);
  }, []);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Connected apps</h1>
          <p className="page__subtitle">
            AI assistants and integrations that have been granted access
            to this tenant via OAuth. Each row is a single connection —
            revoke one without affecting the others. Plaintext tokens
            are never shown here; the external app holds the only copy.
          </p>
        </div>
      </div>

      {loading && (
        <div className="state"><div className="state__title">Loading connections…</div></div>
      )}
      {error && !loading && (
        <div className="state state--error">
          <div className="state__title">Couldn't load connections</div>
          <div>{error}</div>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <EmptyState />
      )}

      {!loading && !error && rows.length > 0 && (
        <ul className="connected-list">
          {rows.map((row) => (
            <ConnectedRow
              key={row.prefix}
              row={row}
              onRevoke={() => onRevoke(row)}
              onReauthorize={() => onReauthorize(row)}
            />
          ))}
        </ul>
      )}

      {confirmNode}
      <style>{CONNECTED_CSS}</style>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="connected-empty">
      <div className="connected-empty__icon" aria-hidden="true">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22v-5" />
          <path d="M9 8V2" />
          <path d="M15 8V2" />
          <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
        </svg>
      </div>
      <div className="connected-empty__title">
        No connected apps yet
      </div>
      <p className="connected-empty__body">
        Connect Claude, Codex, Cursor and other AI assistants to point
        them at this tenant — they'll be able to read your tours,
        author content, and resolve help requests on your behalf. You
        stay in control of which scopes they can use.
      </p>
      <div className="connected-empty__cta">
        <a
          href="/oauth/authorize?client=claude"
          className="btn btn--primary"
        >
          Connect Claude
        </a>
      </div>
    </div>
  );
}

function ConnectedRow({ row, onRevoke, onReauthorize }) {
  const label = appLabel(row);
  const scopes = Array.isArray(row.scopes) ? row.scopes : [];
  const active = row.active !== false;
  return (
    <li className={`connected-row${active ? '' : ' connected-row--inactive'}`}>
      <div className="connected-row__icon" aria-hidden="true">
        {label.charAt(0).toUpperCase()}
      </div>
      <div className="connected-row__body">
        <div className="connected-row__head">
          <span className="connected-row__name">{label}</span>
          <span className={`connected-row__status connected-row__status--${active ? 'ok' : 'revoked'}`}>
            {active ? 'Active' : 'Revoked'}
          </span>
        </div>
        <div className="connected-row__meta">
          <span title="Scopes granted to this app">
            {scopes.length === 0 ? 'full access' : `${scopes.length} scope${scopes.length === 1 ? '' : 's'}`}
          </span>
          {row.project_id && (
            <>
              <span aria-hidden="true">·</span>
              <span title="Project this token is bound to">
                project: <code>{row.project_id}</code>
              </span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span title={`Last call from this connection${row.last_used_at ? ` at ${row.last_used_at}` : ''}`}>
            last used {formatRelative(row.last_used_at) || 'never'}
          </span>
        </div>
        {scopes.length > 0 && (
          <div className="connected-row__scopes">
            {scopes.slice(0, 6).map((s) => (
              <span key={s} className="connected-row__scope">{s}</span>
            ))}
            {scopes.length > 6 && (
              <span className="connected-row__scope connected-row__scope--more">
                +{scopes.length - 6} more
              </span>
            )}
          </div>
        )}
      </div>
      <div className="connected-row__actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onReauthorize}
          disabled={row.is_metadata_only}
          title={
            row.is_metadata_only
              ? "You can see this connection but can't act on it from your role."
              : 'Re-run the OAuth consent flow against the current tenant.'
          }
        >
          Reauthorize
        </button>
        <button
          type="button"
          className="btn btn--danger"
          onClick={onRevoke}
          disabled={row.is_metadata_only || !active}
        >
          {active ? 'Revoke' : 'Revoked'}
        </button>
      </div>
    </li>
  );
}

/**
 * Surface a friendly name for the connected app.  Most rows carry
 * a `name` string the OAuth client wrote at mint time; fall back to
 * a heuristic so a legacy mcp row without metadata still renders
 * something useful instead of the raw key prefix.
 */
function appLabel(row) {
  if (row?.name) return row.name;
  // Lightweight client_id heuristic — the OAuth flow today
  // recognises a handful of well-known clients (claude / codex /
  // cursor / continue).  Future iterations can read the client
  // metadata directly off the row if/when the backend persists it.
  const fromPrefix = (row?.prefix || '').toLowerCase();
  if (fromPrefix.includes('claude')) return 'Claude';
  if (fromPrefix.includes('codex')) return 'Codex';
  if (fromPrefix.includes('cursor')) return 'Cursor';
  return 'Connected app';
}

function oauthClientIdFor(row) {
  // The same heuristic ↑ as appLabel — we don't carry a structured
  // client_id on the api-key row today, so a refresh of the consent
  // flow falls back to the well-known label.
  const label = appLabel(row).toLowerCase();
  if (label.includes('claude')) return 'claude';
  if (label.includes('codex')) return 'codex';
  if (label.includes('cursor')) return 'cursor';
  return label.replace(/\s+/g, '-');
}

function formatRelative(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const CONNECTED_CSS = `
  .connected-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .connected-row {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    gap: 14px;
    align-items: center;
    padding: 14px 18px;
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: 12px;
  }
  .connected-row--inactive {
    opacity: 0.65;
  }
  .connected-row__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
    font-weight: 700;
    font-size: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .connected-row__head {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .connected-row__name {
    font-weight: 700;
    color: var(--text-1, #0f172a);
    font-size: 14.5px;
  }
  .connected-row__status {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 9999px;
  }
  .connected-row__status--ok {
    color: var(--success-700, #15803d);
    background: var(--success-50, #f0fdf4);
  }
  .connected-row__status--revoked {
    color: var(--danger-700, #b91c1c);
    background: var(--danger-50, #fef2f2);
  }
  .connected-row__meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    color: var(--text-3, #64748b);
    font-size: 12.5px;
    margin-top: 4px;
  }
  .connected-row__meta code {
    font-size: 11.5px;
    padding: 0 4px;
    border-radius: 4px;
    background: var(--surface-1, #f1f5f9);
  }
  .connected-row__scopes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
  .connected-row__scope {
    font-size: 11.5px;
    padding: 2px 8px;
    border-radius: 9999px;
    background: var(--surface-1, #f1f5f9);
    color: var(--text-2, #334155);
  }
  .connected-row__scope--more {
    background: transparent;
    border: 1px dashed var(--border-1, #e5e7eb);
  }
  .connected-row__actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .connected-empty {
    background: var(--surface-0, #fff);
    border: 1px dashed var(--border-1, #e5e7eb);
    border-radius: 14px;
    padding: 32px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
  .connected-empty__icon {
    color: var(--brand-600, #4f46e5);
  }
  .connected-empty__title {
    font-size: 16px;
    font-weight: 700;
    color: var(--text-1, #0f172a);
  }
  .connected-empty__body {
    margin: 0;
    max-width: 520px;
    font-size: 13.5px;
    color: var(--text-3, #64748b);
    line-height: 1.5;
  }
  .connected-empty__cta {
    margin-top: 12px;
  }
`;
